import { createFormHook, useStore } from '@tanstack/react-form'
import { Button } from '../ui/button'
import { Suspense, useEffect, useRef } from 'react'
import type React from 'react'
import { useBlocker } from '@tanstack/react-router'
import { fieldContext, formContext, useFormContext } from './form/shared'
import { cn } from '../../lib/utils'
import { Spinner } from '../ui/spinner'
import { Skeleton } from '../ui/skeleton'
import { useLingui } from '@lingui/react/macro'
import { lazyPreloadable, type PreloadableComponent } from '../../lib/lazy-preloadable'

// Eager: small, frequently used fields that should be in the base form chunk.
import TextField from './form/text-field'
import TextareaField from './form/textarea-field'
import SwitchField from './form/switch-field'
import PasswordField from './form/password-field'
import { CheckboxField } from './form/checkbox-field'

// Skeleton for fields laid out as `grid gap-2` + FieldLabelRow (h-5) + control.
// Total height matches a real field: 20 (label row) + 8 (gap) + control height.
function StandardFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
  return (
    <div className="grid w-full gap-2">
      <div className="flex h-5 items-center">
        <Skeleton className="h-3.5 w-24" />
      </div>
      <Skeleton className={cn('h-8 w-full rounded-lg', controlClassName)} />
    </div>
  )
}

// Skeleton for fields laid out as `mb-2` between label and a tall control
// (upload drop zones). Matches their stacked layout exactly.
function StackedFieldSkeleton({ controlClassName }: { controlClassName?: string }) {
  return (
    <div className="w-full">
      <div className="mb-2 flex h-5 items-center">
        <Skeleton className="h-3.5 w-24" />
      </div>
      <Skeleton className={cn('w-full rounded-xl', controlClassName)} />
    </div>
  )
}

// Skeleton for the ImageField `avatar` variant: a circular control (size-28)
// stacked over a short caption line, matching its centered column layout.
function AvatarFieldSkeleton() {
  return (
    <div className="flex flex-col items-center gap-2">
      <Skeleton className="m-px size-28 rounded-full" />
      <Skeleton className="h-3.5 w-20" />
    </div>
  )
}

// Skeleton for fields laid out as `flex items-start gap-3` (horizontal orientation):
// label column (w-48 h-8) + flex-1 control (h-8).
function HorizontalFieldSkeleton() {
  return (
    <div className="flex items-start gap-3">
      <Skeleton className="h-8 w-48 shrink-0 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
    </div>
  )
}

// Wrap a lazy field with a Suspense boundary so every parent form gets a
// per-field skeleton for free while the chunk is loading. The component
// exposes `.preload()` (via lazyPreloadable) so consumers can warm the chunks
// during browser idle and skip the skeleton flash on dialog open.
type LazyField<P extends object> = React.ComponentType<P> & { preload: () => Promise<unknown> }

function lazyField<P extends object>(
  loader: () => Promise<{ default: React.ComponentType<P> }>,
  fallback: React.ReactNode | ((props: P) => React.ReactNode) = <StandardFieldSkeleton />,
): LazyField<P> {
  const LazyComponent: PreloadableComponent<React.ComponentType<P>> = lazyPreloadable(loader)
  function LazyField(props: P) {
    const fb = typeof fallback === 'function' ? fallback(props) : fallback
    return (
      <Suspense fallback={fb}>
        <LazyComponent {...props} />
      </Suspense>
    )
  }
  ;(LazyField as LazyField<P>).preload = LazyComponent.preload
  return LazyField as LazyField<P>
}

const orientationSkeleton = (props: { orientation?: 'vertical' | 'horizontal' }) =>
  props.orientation === 'horizontal' ? <HorizontalFieldSkeleton /> : <StandardFieldSkeleton />

// Each lazy field is wrapped in its own Suspense boundary with a skeleton
// fallback sized to match the resolved field, avoiding CLS.
const DateField = lazyField(() => import('./form/date-field'), orientationSkeleton)
const DateTimeField = lazyField(() => import('./form/datetime-field'))
const ColorField = lazyField(() => import('./form/color-field'))
const FileField = lazyField(() => import('./form/file-field'))
const ImageField = lazyField(
  () => import('./form/image-field'),
  (props: { variant?: 'default' | 'avatar' }) =>
    props.variant === 'avatar' ? <AvatarFieldSkeleton /> : <StandardFieldSkeleton />,
)
const ImageUploadField = lazyField(
  () => import('./form/image-upload-field'),
  <StackedFieldSkeleton controlClassName="h-[198px]" />,
)
const SelectTextField = lazyField(() => import('./form/select-text-field'), orientationSkeleton)
const SelectNumberField = lazyField(() => import('./form/select-number-field'), orientationSkeleton)
const ComboboxTextField = lazyField(() => import('./form/combobox-text-field'), orientationSkeleton)
const ComboboxNumberField = lazyField(() => import('./form/combobox-number-field'), orientationSkeleton)
const MultiComboboxTextField = lazyField(() => import('./form/multi-combobox-text-field'), orientationSkeleton)
const MultiComboboxPopoverTextField = lazyField(
  () => import('./form/multi-combobox-popover-text-field'),
  orientationSkeleton,
)
const MultiComboboxNumberField = lazyField(() => import('./form/multi-combobox-number-field'), orientationSkeleton)
const SimpleSelectField = lazyField(() => import('./form/simple-select-field'), orientationSkeleton)
const SimpleSelectNumberField = lazyField(() => import('./form/simple-select-number-field'), orientationSkeleton)
const CountryField = lazyField(() => import('./form/country-field'), orientationSkeleton)
const ProvinceField = lazyField(() => import('./form/province-field'), orientationSkeleton)

export const fileToBase64 = async (file: File | undefined): Promise<string | undefined> =>
  file instanceof File
    ? new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        // oxlint-disable-next-line prefer-add-event-listener
        reader.onload = () => resolve(reader.result as string)
        // oxlint-disable-next-line prefer-add-event-listener
        reader.onerror = reject
      })
    : undefined

function SubmitButton({ label, ...props }: React.ComponentProps<typeof Button> & { label: string }) {
  const form = useFormContext()
  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <Button type="submit" {...props} disabled={props.disabled ?? isSubmitting}>
          {isSubmitting && <Spinner />}
          {label}
        </Button>
      )}
    </form.Subscribe>
  )
}

function SaveDiscardButtons(props: { className?: string }) {
  const { t } = useLingui()
  const form = useFormContext()
  const { isSubmitting, isDefaultValue } = useStore(form.store, (state) => ({
    isDefaultValue: state.isDefaultValue,
    isSubmitting: state.isSubmitting,
  }))

  useBlocker({
    shouldBlockFn: () => {
      if (isDefaultValue) return false

      const shouldLeave = confirm(t`You have unsaved changes, are you sure you want to leave?`)
      return !shouldLeave
    },
    enableBeforeUnload: !isDefaultValue,
  })

  if (isDefaultValue) return null

  return (
    <div className={cn('flex gap-2', props.className)}>
      <Button
        type="reset"
        variant="secondary"
        onClick={(event) => {
          event.preventDefault()
          form.reset()
          // TODO: We need to double reset to remove all translated campaign texts with the translate feature,
          // is totally a bug on tanstack/form
          setTimeout(() => form.reset())
        }}
      >
        {t`Discard`}
      </Button>
      <Button disabled={isSubmitting}>
        {isSubmitting && <Spinner />}
        {t`Save`}
      </Button>
    </div>
  )
}

const { useAppForm: _useAppForm } = createFormHook({
  fieldComponents: {
    TextField,
    TextareaField,
    DateField,
    DateTimeField,
    ColorField,
    FileField,
    ImageField,
    ImageUploadField,
    SelectTextField,
    SelectNumberField,
    ComboboxTextField,
    ComboboxNumberField,
    MultiComboboxTextField,
    MultiComboboxPopoverTextField,
    MultiComboboxNumberField,
    SimpleSelectField,
    SimpleSelectNumberField,
    CountryField,
    ProvinceField,
    CheckboxField,
    SwitchField,
    PasswordField,
  },
  formComponents: {
    SubmitButton,
    SaveDiscardButtons,
  },
  fieldContext,
  formContext,
})

function useAppFormWithScrollToError(...args: Parameters<typeof _useAppForm>): ReturnType<typeof _useAppForm> {
  const form = _useAppForm(...args)

  const submissionAttempts = useStore(form.store, (state: { submissionAttempts: number }) => state.submissionAttempts)
  const prevAttempts = useRef(0)
  useEffect(() => {
    if (submissionAttempts > prevAttempts.current) {
      prevAttempts.current = submissionAttempts
      const id = setTimeout(() => {
        const firstError = document.querySelector<HTMLElement>('[aria-invalid="true"]')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          firstError.focus({ preventScroll: true })
        }
      }, 0)
      return () => clearTimeout(id)
    }
  }, [submissionAttempts])

  return form
}

export const useAppForm = useAppFormWithScrollToError as unknown as typeof _useAppForm
